import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AuthGate.css';
import { useAuth } from '../contexts/AuthContext';
import { apiPost, translateError } from '../services/apiService';
import SEO from './SEO';

const NESTJS_URL = process.env.REACT_APP_NESTJS_URL || 'http://localhost:4000';

export default function AuthGate() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { accessToken } = await apiPost<{ accessToken: string }>('/auth/login', { email, password });
      login(accessToken);
      navigate('/app', { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      setError(translateError(err, 'Não foi possível fazer login. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-gate">
      <SEO
        title="Entrar"
        description="Faça login no MedPuzzle para acessar simulações de diagnóstico médico com IA."
        path="/login"
      />
      <div className="auth-card">
        <div className="auth-icon-wrap" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.3.3 0 1 0 .2.3" />
            <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
            <circle cx="20" cy="10" r="2" />
          </svg>
        </div>
        <div className="auth-header">
          <h1 className="auth-card-title">Bem-vindo de volta</h1>
          <p className="auth-card-desc">Entre para continuar</p>
        </div>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          {error && <p className="auth-error" id="auth-error">{error}</p>}
        </div>
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="email">E-mail</label>
            <div className="auth-input-wrapper">
              <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="voce@exemplo.com"
                disabled={loading}
                aria-describedby={error ? 'auth-error' : undefined}
                aria-invalid={error ? true : undefined}
              />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="password">Senha</label>
            <div className="auth-input-wrapper">
              <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
                aria-describedby={error ? 'auth-error' : undefined}
                aria-invalid={error ? true : undefined}
              />
            </div>
          </div>
          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
        <div className="auth-divider">
          <span>ou</span>
        </div>
        <button
          type="button"
          className="auth-google-btn"
          onClick={() => { window.location.href = `${NESTJS_URL}/auth/google`; }}
          disabled={loading}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Entrar com Google
        </button>
        <p className="auth-disclaimer">
          Apenas para fins educacionais e de treinamento. Não para uso clínico.
        </p>
      </div>
    </div>
  );
}
